import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import {
    MarkovRedistribution,
    MarkovRedistributionCreate,
} from "server/models"
import {
    useInput,
    StateType,
} from "client/utils"
import {
    StoreType,
    MarkovRedistributionValidation,
    SaveMarkovRedistribution,
    setMarkovRedistributionEdit,
    setMarkovRedistributionReloadList,
    setMarkovRedistributionReloadEdit,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    LabelValue,
    Input,
    CheckButton,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditRedistributionProps {
    redistribution: MarkovRedistribution
}

let isMounted: boolean

const Form = ({
    redistribution,
}: EditRedistributionProps) => {
    const model = useSelector((state: StoreType) => state.markovModel.modelView)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(redistribution.name)
    const [periodic, setPeriodic] = useState(redistribution.periodic)
    const [time, setTime] = useState(redistribution.time)
    const [order, setOrder] = useState(redistribution.order)

    const [validation, setValidation] = useState<MarkovRedistributionValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (isMounted && modals.length) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newRedistributionData: MarkovRedistributionCreate = {
            modelId: redistribution.modelId || model.id,
            name,
            periodic,
            time,
            order,
        }

        SaveMarkovRedistribution(
            redistribution.id,
            newRedistributionData,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setMarkovRedistributionEdit({
                id: res.newRedistributionId || redistribution.id,
                ...newRedistributionData,
            }))

            dispatch(setMarkovRedistributionReloadList(false))
            setTimeout(() => {
                dispatch(setMarkovRedistributionReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setMarkovRedistributionReloadEdit(false))
                setTimeout(() => {
                    dispatch(setMarkovRedistributionReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>
                            {lang.time}
                            <LabelValue>{lang.hoursLabel}</LabelValue>
                        </Label>
                        <Input
                            type="text"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                            onBlur={(e) => {
                                const parsedValue = parseInt(e.target.value, 10)
                                if (Number.isNaN(parsedValue)) setTime("")
                                else if (parsedValue >= 0) setTime(parsedValue.toString())
                                else setTime("")
                            }}
                        />
                    </FormGroup>
                    <FormGroup>
                        <CheckButton
                            title={lang.periodic}
                            checked={periodic}
                            onChange={() => setPeriodic(!periodic)}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.order}</Label>
                        <Input
                            type="text"
                            value={order !== null ? order : ""}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10)
                                if (!Number.isNaN(parsed)) setOrder(parsed)
                                else setOrder(null)
                            }}
                        />
                        <Feedback>{validation && validation.order}</Feedback>
                    </FormGroup>
                </Col>

                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
